import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Badge,
  Box,
  BreadcrumbGroup,
  Button,
  ColumnLayout,
  Container,
  ContentLayout,
  Header,
  Link,
  SpaceBetween,
  StatusIndicator,
  Toggle,
} from '@amzn/awsui-components-console';
import { useParams, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import cronstrue from 'cronstrue';
import { getUserAtom } from '../../contexts/userContext';
import { makeApiCall } from '../../utils/api';
import { API_PATHS } from '../../config/api-endpoints';
import { transformSchedule } from '../../utils/schedule-transformer';
import { useProfile } from '../../hooks/useProfiles';
import { Schedule, ScheduleStatus } from '../../types';
import EditScheduleModal from '../molecules/EditScheduleModal';
import DeleteConfirmModal from '../molecules/DeleteConfirmModal';
import { ScheduleFormData } from '../../hooks/useSchedules';
import NProgress from 'nprogress';
import './ScheduleDetailsPage.scss';

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short',
    });
  } catch { return dateString; }
};

const getCronDescription = (cronExpression: string): string => {
  try { return cronstrue.toString(cronExpression, { use24HourTimeFormat: false }); }
  catch { return cronExpression; }
};

const StatusBadge: React.FC<{ status: ScheduleStatus }> = ({ status }) => {
  switch (status) {
    case 'ENABLED': return <StatusIndicator type="success">Enabled</StatusIndicator>;
    case 'DISABLED': return <StatusIndicator type="stopped">Disabled</StatusIndicator>;
    case 'AUTO_DISABLED': return <StatusIndicator type="error">Auto-Disabled</StatusIndicator>;
    default: return <StatusIndicator>{status}</StatusIndicator>;
  }
};

const isMockDataEnabled = (): boolean => {
  const settings = (window as any).__ADDAPT_SETTINGS__;
  return settings?.features?.enableMockData === true;
};

const mockSchedule: Schedule = {
  scheduleId: 'schedule-1', jobId: 'job-1', scheduleName: 'Daily Morning Run',
  description: 'Runs every day at 8 AM UTC', scheduleExpression: 'cron(0 8 * * ? *)',
  frequencyType: 'CRON', timezone: 'UTC', status: 'ENABLED',
  nextExecutionTime: new Date(Date.now() + 86400000).toISOString(),
  createdAt: '2024-01-01T00:00:00Z', updatedAt: '2024-01-15T10:30:00Z', createdBy: 'testuser',
};

const ScheduleDetailsPage: React.FC = () => {
  const { id: profileId, jobId, scheduleId } = useParams();
  const navigate = useNavigate();
  const [authenticatedUser] = useAtom(getUserAtom);
  const { profile: profileData } = useProfile(profileId || null);

  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [jobData, setJobData] = useState<{ id: string; name: string } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchSchedule = useCallback(async () => {
    if (!scheduleId) return;
    setScheduleLoading(true);
    setScheduleError(null);
    try {
      if (isMockDataEnabled()) {
        await new Promise((r) => setTimeout(r, 300));
        setSchedule(mockSchedule);
        return;
      }
      const response = await makeApiCall(API_PATHS.SCHEDULES.GET, 'POST', { jobId, scheduleId });
      setSchedule(transformSchedule(response.schedule || response));
    } catch (err: any) {
      setScheduleError(err.message || 'Failed to load schedule');
      if (isMockDataEnabled() && err.message?.includes('404')) {
        setSchedule(mockSchedule);
        setScheduleError(null);
      }
    } finally {
      setScheduleLoading(false);
    }
  }, [scheduleId, jobId]);

  const fetchJobDetails = useCallback(async () => {
    if (!jobId || !authenticatedUser) return;
    try {
      const response = await makeApiCall(API_PATHS.JOBS.GET, 'POST', { userId: authenticatedUser.username, jobId });
      if (response.job) setJobData({ id: response.job.jobId, name: response.job.jobName });
    } catch { /* non-blocking */ }
  }, [jobId, authenticatedUser]);

  useEffect(() => { fetchSchedule(); fetchJobDetails(); }, [fetchSchedule, fetchJobDetails]);

  const handleRefresh = () => { NProgress.start(); fetchSchedule().finally(() => NProgress.done()); };

  const handleToggle = async (enabled: boolean) => {
    if (!schedule) return;
    setToggling(true);
    const previousStatus = schedule.status;
    setSchedule((prev) => (prev ? { ...prev, status: enabled ? 'ENABLED' : 'DISABLED' } : prev));
    try {
      if (!isMockDataEnabled()) {
        await makeApiCall(API_PATHS.SCHEDULES.UPDATE_STATUS, 'POST', { jobId, scheduleId: schedule.scheduleId, enabled });
      }
      setNotification({ type: 'success', message: `Schedule ${enabled ? 'enabled' : 'disabled'} successfully` });
    } catch (err: any) {
      setSchedule((prev) => (prev ? { ...prev, status: previousStatus } : prev));
      setNotification({ type: 'error', message: err.message || 'Failed to toggle schedule' });
    } finally { setToggling(false); }
  };

  const handleEditSchedule = async (data: ScheduleFormData) => {
    if (!schedule) return;
    try {
      if (isMockDataEnabled()) {
        setSchedule((prev) => prev ? { ...prev, scheduleName: data.scheduleName, description: data.description, scheduleExpression: data.scheduleExpression, frequencyType: data.frequencyType, updatedAt: new Date().toISOString() } : prev);
        setNotification({ type: 'success', message: 'Schedule updated successfully' });
        setShowEditModal(false);
        return;
      }
      const response = await makeApiCall(API_PATHS.SCHEDULES.UPDATE, 'POST', { jobId, scheduleId: schedule.scheduleId, scheduleName: data.scheduleName, description: data.description, scheduleExpression: data.scheduleExpression, frequencyType: data.frequencyType, timezone: data.timezone });
      setSchedule(transformSchedule(response.schedule || response));
      setNotification({ type: 'success', message: 'Schedule updated successfully' });
      setShowEditModal(false);
    } catch (err: any) { throw err; }
  };

  const handleDeleteSchedule = async () => {
    if (!schedule) return;
    try {
      if (!isMockDataEnabled()) {
        await makeApiCall(API_PATHS.SCHEDULES.DELETE, 'POST', { jobId, scheduleId: schedule.scheduleId });
      }
      navigate(`/profile/${profileId}/job/${jobId}/schedules`);
    } catch (err: any) { throw err; }
  };

  const breadcrumbs = [
    { text: 'Home', href: '/' },
    { text: profileData?.name || 'Profile', href: `/profile/${profileId}` },
    { text: 'Jobs', href: `/profile/${profileId}/jobs` },
    { text: jobData?.name || jobId || 'Job', href: `/profile/${profileId}/job/${jobId}` },
    { text: 'Schedules', href: `/profile/${profileId}/job/${jobId}/schedules` },
  ];

  if (scheduleLoading && !schedule) {
    return (
      <ContentLayout defaultPadding={false} headerVariant="high-contrast"
        header={<Box padding={{ horizontal: 'xxxl', vertical: 'l' }}><SpaceBetween size="m">
          <BreadcrumbGroup items={[...breadcrumbs, { text: 'Loading...', href: '#' }]} ariaLabel="Breadcrumbs" />
          <Box fontSize="heading-xl" fontWeight="bold" color="inherit">Loading schedule...</Box>
        </SpaceBetween></Box>}>
        <Box padding={{ horizontal: 'xl', vertical: 'l' }}>
          <StatusIndicator type="loading">Loading schedule details...</StatusIndicator>
        </Box>
      </ContentLayout>
    );
  }

  if (scheduleError && !schedule) {
    return (
      <ContentLayout defaultPadding={false} headerVariant="high-contrast"
        header={<Box padding={{ horizontal: 'xxxl', vertical: 'l' }}><SpaceBetween size="m">
          <BreadcrumbGroup items={[...breadcrumbs, { text: 'Error', href: '#' }]} ariaLabel="Breadcrumbs" />
          <Box fontSize="heading-xl" fontWeight="bold" color="inherit">Schedule Not Found</Box>
        </SpaceBetween></Box>}>
        <Box padding={{ horizontal: 'xl', vertical: 'l' }}>
          <Alert type="error" header="Error loading schedule"
            action={<Button onClick={() => navigate(`/profile/${profileId}/job/${jobId}/schedules`)}>Back to Schedules</Button>}>
            {scheduleError}
          </Alert>
        </Box>
      </ContentLayout>
    );
  }

  return (
    <div className="schedule-details-page">
      <ContentLayout defaultPadding={false} headerVariant="high-contrast"
        header={
          <Box padding={{ horizontal: 'xxxl', vertical: 'l' }}>
            <SpaceBetween size="m" direction="vertical">
              <BreadcrumbGroup items={[...breadcrumbs, { text: schedule?.scheduleName || 'Schedule', href: '#' }]} ariaLabel="Breadcrumbs" />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                <div style={{ flex: 1 }}>
                  <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                    <Box fontSize="heading-xl" fontWeight="bold" color="inherit">
                      {schedule?.scheduleName || 'Schedule Details'}
                    </Box>
                    {schedule && <StatusBadge status={schedule.status} />}
                  </SpaceBetween>
                  {schedule && (
                    <Box fontSize="body-m" color="inherit" margin={{ top: 'xs' }}>
                      <SpaceBetween size="s" direction="horizontal" alignItems="center">
                        <span>{getCronDescription(schedule.scheduleExpression.replace(/^cron\(/, '').replace(/\)$/, ''))}</span>
                        <Badge color="blue">UTC</Badge>
                      </SpaceBetween>
                    </Box>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto', alignItems: 'center' }}>
                  {schedule && (
                    <Toggle checked={schedule.status === 'ENABLED'} onChange={({ detail }) => handleToggle(detail.checked)}
                      disabled={toggling || schedule.status === 'AUTO_DISABLED'}>
                      {schedule.status === 'ENABLED' ? 'Enabled' : schedule.status === 'AUTO_DISABLED' ? 'Auto-Disabled' : 'Disabled'}
                    </Toggle>
                  )}
                  <Button iconName="refresh" onClick={handleRefresh} loading={scheduleLoading} />
                  <Button iconName="edit" onClick={() => setShowEditModal(true)}>Edit</Button>
                  <Button iconName="remove" onClick={() => setShowDeleteModal(true)}>Delete</Button>
                </div>
              </div>
            </SpaceBetween>
          </Box>
        }
      >
        <Box padding={{ horizontal: 'xl', vertical: 'l' }}>
          <SpaceBetween size="l" direction="vertical">
            {notification && (
              <Alert type={notification.type} dismissible onDismiss={() => setNotification(null)}>
                {notification.message}
              </Alert>
            )}

            {schedule?.status === 'AUTO_DISABLED' && (
              <Alert type="error" header="Schedule Auto-Disabled"
                action={<Button onClick={() => handleToggle(true)} loading={toggling}>Re-enable Schedule</Button>}>
                This schedule was automatically disabled after {schedule.consecutiveFailures || 3} consecutive failures.
                {schedule.lastFailureReason && (
                  <Box margin={{ top: 's' }}><Box variant="awsui-key-label">Last Failure Reason:</Box><Box>{schedule.lastFailureReason}</Box></Box>
                )}
                {schedule.autoDisabledAt && (
                  <Box margin={{ top: 's' }}><Box variant="awsui-key-label">Auto-Disabled At:</Box><Box>{formatDate(schedule.autoDisabledAt)}</Box></Box>
                )}
              </Alert>
            )}

            {schedule && (
              <Container header={<Header variant="h2">Schedule Configuration</Header>}>
                <ColumnLayout columns={4} variant="text-grid">
                  <div><Box variant="awsui-key-label">Next Execution</Box><Box>{formatDate(schedule.nextExecutionTime)}</Box></div>
                  <div><Box variant="awsui-key-label">Last Execution</Box><Box>{formatDate(schedule.lastExecutionTime)}</Box></div>
                  <div><Box variant="awsui-key-label">Created</Box><Box>{formatDate(schedule.createdAt)}</Box></div>
                  <div><Box variant="awsui-key-label">Created By</Box><Box>{schedule.createdBy}</Box></div>
                </ColumnLayout>
                {schedule.description && (
                  <Box margin={{ top: 'm' }}><Box variant="awsui-key-label">Description</Box><Box>{schedule.description}</Box></Box>
                )}
                {schedule.consecutiveFailures !== undefined && schedule.consecutiveFailures > 0 && (
                  <Box margin={{ top: 'm' }}>
                    <ColumnLayout columns={3} variant="text-grid">
                      <div><Box variant="awsui-key-label">Consecutive Failures</Box><Box color="text-status-error">{schedule.consecutiveFailures}</Box></div>
                      {schedule.lastFailureAt && <div><Box variant="awsui-key-label">Last Failure At</Box><Box>{formatDate(schedule.lastFailureAt)}</Box></div>}
                      {schedule.lastFailureReason && <div><Box variant="awsui-key-label">Last Failure Reason</Box><Box>{schedule.lastFailureReason}</Box></div>}
                    </ColumnLayout>
                  </Box>
                )}
                <Box margin={{ top: 'l' }}>
                  <Button variant="link" iconName="external" onClick={() => navigate(`/profile/${profileId}/job/${jobId}/history`)}>
                    View run history for this job
                  </Button>
                </Box>
              </Container>
            )}
          </SpaceBetween>
          <Box padding={{ bottom: 'xxxl' }} />
        </Box>
      </ContentLayout>

      {schedule && (
        <EditScheduleModal visible={showEditModal} onDismiss={() => setShowEditModal(false)}
          onSubmit={handleEditSchedule} schedule={schedule} jobId={jobId || ''} />
      )}
      <DeleteConfirmModal visible={showDeleteModal} onDismiss={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteSchedule} schedule={schedule} />
    </div>
  );
};

export default ScheduleDetailsPage;
